import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Headset, MessagesSquare } from 'lucide-react';

import { CommentThreadSkeleton } from '@/components/skeletons';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { buildDayDividerItems } from '@/features/inbox/utils/day-divider';
import { cn } from '@/lib/utils';
import { formatTimeOfDay } from '@/utils/date-time';
import { getInitials } from '@/utils/string';
import {
    BUG_REPORT_STATUS_META,
    BUG_REPORT_TYPE_META,
    BUG_REPORT_VARIABLE_GROUPS,
} from '../constants';
import { useAddBugReportComment, useBugReportDetail } from '../hooks/use-bug-reports';
import type { IBugReportAttachment, IBugReportComment, IBugReportDetail } from '../types';
import { MessageAttachmentList } from './message-attachment-list';
import { ReportComposer } from './report-composer';

/**
 * A day divider between two messages. Rendered as a list item so the thread
 * stays one `<ul>` and the separator is not read as a message.
 *
 * Only the markup is local — the grouping and the label come from the inbox's
 * shared `buildDayDividerItems`, so this thread breaks on days and names them
 * exactly like the inbox thread does.
 */
function DayDivider({ label }: { readonly label: string }) {
    return (
        <li aria-hidden className="flex items-center gap-2 py-0.5">
            <span className="h-px flex-1 bg-border-default" />
            <span className="text-2xs font-medium text-text-tertiary">{label}</span>
            <span className="h-px flex-1 bg-border-default" />
        </li>
    );
}

interface IMessageBubbleProps {
    readonly isSupport: boolean;
    /** Reporter's display name — drives their avatar initials. Unused for support. */
    readonly authorName: string;
    readonly body: string;
    readonly createdAt: string;
    readonly attachments: readonly IBugReportAttachment[];
}

/**
 * One message in the thread: round avatar, bubble, any files, timestamp.
 *
 * The avatar is what says who is speaking, so there is no name label above the
 * bubble — support wears the headset it wears everywhere else in this feature
 * (see `TYPE_ICONS` in `my-reports-list.tsx`), the reporter their own initials.
 * Side still mirrors the super admin portal: support left, reporter right.
 */
function MessageBubble({
    isSupport,
    authorName,
    body,
    createdAt,
    attachments,
}: IMessageBubbleProps) {
    const { t } = useTranslation();

    const authorLabel = isSupport
        ? t('bugReports.thread.supportAuthor', 'Support team')
        : t('bugReports.thread.youAuthor', 'You');

    return (
        <li className={cn('flex items-start gap-2', isSupport ? 'flex-row' : 'flex-row-reverse')}>
            <Avatar size="sm" aria-label={authorLabel} title={authorLabel}>
                <AvatarFallback
                    className={cn(
                        'text-2xs',
                        isSupport
                            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200'
                            : 'bg-neutral-100 text-text-secondary dark:bg-neutral-800',
                    )}
                >
                    {isSupport ? (
                        <Headset className="size-3" aria-hidden />
                    ) : (
                        getInitials(authorName)
                    )}
                </AvatarFallback>
            </Avatar>

            <div
                className={cn(
                    'flex min-w-0 flex-1 flex-col gap-1',
                    isSupport ? 'items-start' : 'items-end',
                )}
            >
                {/* Skipped entirely for an attachment-only reply — a bubble
                    with no text in it reads as a message that failed to send.
                    `w-fit` keeps a two-character reply from getting a bubble as
                    wide as the thread. */}
                {body.length > 0 && (
                    <div
                        className={cn(
                            'w-fit max-w-[85%] rounded-lg border px-3 py-2 text-sm break-words whitespace-pre-wrap',
                            isSupport
                                ? 'border-border-default bg-bg-raised text-text-primary'
                                : 'border-primary-200 bg-primary-50 text-text-primary dark:border-primary-800 dark:bg-primary-900/20',
                        )}
                    >
                        {body}
                    </div>
                )}

                <MessageAttachmentList
                    attachments={attachments}
                    align={isSupport ? 'start' : 'end'}
                />

                <span
                    className="text-2xs text-text-tertiary"
                    title={new Date(createdAt).toLocaleString()}
                >
                    {formatTimeOfDay(createdAt)}
                </span>
            </div>
        </li>
    );
}

/**
 * What the composer's merge tags resolve to, from the report being replied to.
 *
 * Null while the detail request is in flight — the composer is rendered either
 * way, so an unresolved token simply passes through as typed rather than
 * resolving to the string "undefined".
 */
function buildReportVariableValues(report: IBugReportDetail | undefined): Record<string, string> {
    if (!report) return {};

    return {
        report_title: report.title,
        report_type: BUG_REPORT_TYPE_META[report.type].label,
        report_status: BUG_REPORT_STATUS_META[report.status].label,
        report_id: report.id,
        page_url: report.pageUrl ?? '',
        my_name: report.reportedBy.name,
        my_email: report.reportedBy.email ?? '',
        organization_name: report.organization.name,
    };
}

interface IReportConversationProps {
    readonly reportId: string;
    /** The report's own body — rendered as the first message in the thread. */
    readonly description: string;
    readonly createdAt: string;
}

/**
 * The chat between the reporter and the support team for one report.
 *
 * The report's own description opens the thread rather than sitting in a
 * separate block above it: to the reporter it IS the first message, and
 * splitting the two makes the support reply below read as a non-sequitur.
 *
 * Support replies sit left and the reporter's own right — the inverse of the
 * super admin portal, where the same thread is mirrored for the other side.
 */
export function ReportConversation({ reportId, description, createdAt }: IReportConversationProps) {
    const { t } = useTranslation();

    const { data, isLoading, isError, refetch } = useBugReportDetail(reportId);
    const addComment = useAddBugReportComment(reportId);

    const threadRef = useRef<HTMLUListElement>(null);
    const messageCount = data?.comments.length ?? 0;

    // Whose initials go in the reporter's avatar. The detail response names the
    // reporter; until it lands, fall back to the label rather than rendering a
    // "?" that then silently changes under the reader.
    const reporterName = data?.reportedBy.name ?? t('bugReports.thread.youAuthor', 'You');

    const variableValues = useMemo(() => buildReportVariableValues(data), [data]);

    /**
     * The report's description and every reply, as one ordered list.
     *
     * Flattened here rather than rendered in two passes so the day dividers can
     * be computed across the whole thread — the boundary between the report and
     * the first reply is exactly where the first divider usually falls.
     */
    const messages = useMemo(() => {
        const opening = {
            key: 'report',
            isSupport: false,
            authorName: reporterName,
            body: description,
            createdAt,
            attachments: data?.attachments ?? [],
        };

        const replies = (data?.comments ?? []).map((comment: IBugReportComment) => ({
            key: comment.id,
            isSupport: comment.authorKind === 'SUPER_ADMIN',
            authorName: comment.author.name,
            body: comment.body,
            createdAt: comment.createdAt,
            attachments: comment.attachments ?? [],
        }));

        return [opening, ...replies];
    }, [createdAt, data, description, reporterName]);

    /**
     * The same thread with a day divider interleaved at each local-day boundary.
     *
     * Uses the inbox's `buildDayDividerItems` rather than comparing days here:
     * the reporter sees this thread and the inbox thread in the same product, so
     * "Today" / "Yesterday" / weekday / "9th May 2026" has to break the same way
     * in both — and those labels are the ones that are actually translated.
     */
    const renderItems = useMemo(
        () =>
            buildDayDividerItems(
                messages,
                (message) => new Date(message.createdAt),
                (message) => message.key,
                t,
            ),
        [messages, t],
    );

    // Pin the thread to its newest message. Without this a long conversation
    // opens on the ORIGINAL report — the reporter lands on what they already
    // know and has to scroll to find the reply they were notified about — and
    // a reply arriving over the socket while they read would appear silently
    // below the fold.
    useEffect(() => {
        const thread = threadRef.current;
        if (!thread) return;
        thread.scrollTop = thread.scrollHeight;
    }, [messageCount]);

    return (
        <div className="flex flex-col gap-3">
            {/* The thread scrolls inside its own box rather than growing the
                dialog: past a few replies the composer was pushed off the
                bottom, so answering meant scrolling the whole dialog first.
                `max-h` only engages once there is enough to scroll, so a short
                thread still renders at its natural height. */}
            <ul
                ref={threadRef}
                className="flex max-h-72 flex-col gap-3 overflow-y-auto overscroll-contain pr-1"
            >
                {/* A divider opens each new calendar day, so a thread that ran
                    over a week reads as a conversation with dates rather than a
                    wall of times. The first one always renders — it dates the
                    original report, which is otherwise the only message with no
                    context above it. */}
                {renderItems.map((item) =>
                    item.kind === 'divider' ? (
                        <DayDivider key={item.key} label={item.label} />
                    ) : (
                        <MessageBubble
                            key={item.key}
                            isSupport={item.value.isSupport}
                            authorName={item.value.authorName}
                            body={item.value.body}
                            createdAt={item.value.createdAt}
                            attachments={item.value.attachments}
                        />
                    ),
                )}

                {isLoading && (
                    <li>
                        <CommentThreadSkeleton comments={2} />
                    </li>
                )}
            </ul>

            {isError ? (
                <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border-default px-4 py-4 text-center">
                    <p className="text-xs text-text-secondary">
                        {t('bugReports.thread.error', 'Failed to load this conversation.')}
                    </p>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void refetch()}
                    >
                        {t('common.retry', 'Retry')}
                    </Button>
                </div>
            ) : (
                !isLoading &&
                data?.comments.length === 0 && (
                    <p className="flex items-center gap-1.5 text-xs text-text-tertiary">
                        <MessagesSquare className="size-3.5" />
                        {t(
                            'bugReports.thread.empty',
                            'No replies yet — our team will get back to you here.',
                        )}
                    </p>
                )
            )}

            <ReportComposer
                variables={BUG_REPORT_VARIABLE_GROUPS}
                variableValues={variableValues}
                isSending={addComment.isPending}
                onSend={(body, attachments) => addComment.mutateAsync({ body, attachments })}
            />
        </div>
    );
}
